import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui";
import {
  BillingInterval,
  SeatPlanKey,
  SEAT_LIMITS,
  canAdjustSeatCount,
  computeBilledLine,
  computeTotalPlanPriceMonthly,
  formatPlanDisplayName,
  getPlanIncludedSeats,
  resolveDefaultTargetSeatCount,
  resolveSeatCount,
} from "../../utils/seats";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (seatCount: number) => Promise<void>;
  currentPlanKey: string;
  currentInterval: string;
  currentSeatCount: number;
  targetPlanKey: SeatPlanKey;
  targetInterval: BillingInterval;
  targetPlanName: string;
  currencySymbol?: string;
  /**
   * "schedule" → switching between active paid plans (proration/scheduling).
   * "new" → user has no active plan; this starts a fresh subscription/trial.
   * "seats" → adjust seats on the current paid Professional plan (charge extras now).
   */
  mode?: "schedule" | "new" | "seats";
  /** When true (only meaningful in "new" mode), the new subscription starts as a free trial. */
  isTrial?: boolean;
  isLaunchFreeBilling?: boolean;
  launchTrialLabel?: string;
  pendingPlanChange?: {
    targetPlanKey?: string;
    targetInterval?: string;
    targetSeatCount?: number | null;
    effectiveAt?: string | Date | null;
  } | null;
};

const PlanChangeModal = ({
  open,
  onClose,
  onConfirm,
  currentPlanKey,
  currentInterval,
  currentSeatCount,
  targetPlanKey,
  targetInterval,
  targetPlanName,
  currencySymbol = "$",
  mode = "schedule",
  isTrial = false,
  isLaunchFreeBilling = false,
  launchTrialLabel = "7-day free trial",
  pendingPlanChange = null,
}: Props) => {
  const trialDurationLabel = isLaunchFreeBilling
    ? launchTrialLabel
    : "7-day free trial";
  const isNewPlanMode = mode === "new";
  const isSeatsMode = mode === "seats";
  const limits = SEAT_LIMITS[targetPlanKey];
  // Trials cannot buy extras — lock to included seats.
  const isFixedSeatPlan = !canAdjustSeatCount(targetPlanKey) || Boolean(isTrial);
  const defaultSeatCount = useMemo(() => {
    if (isTrial) return getPlanIncludedSeats(targetPlanKey);
    if (isSeatsMode) return resolveSeatCount(targetPlanKey, currentSeatCount);
    return resolveDefaultTargetSeatCount(currentPlanKey, targetPlanKey, currentSeatCount);
  }, [currentPlanKey, currentSeatCount, isSeatsMode, isTrial, targetPlanKey]);
  const [seatCount, setSeatCount] = useState(defaultSeatCount);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSeatCount(defaultSeatCount);
    setIsSubmitting(false);
  }, [open, defaultSeatCount]);

  const totalMonthly = computeTotalPlanPriceMonthly(targetPlanKey, targetInterval, seatCount) ?? 0;
  const billedLine = computeBilledLine(targetPlanKey, targetInterval, seatCount);
  const isIntervalOnlyChange =
    String(currentPlanKey).toLowerCase() === targetPlanKey &&
    String(currentInterval).toLowerCase() !== targetInterval;
  const currentSeats = resolveSeatCount(currentPlanKey, currentSeatCount);
  const extraSeatPrice = limits.extraSeatMonthly;
  const seatDelta = Math.max(0, seatCount - currentSeats);
  const chargeNow = isSeatsMode ? seatDelta * extraSeatPrice : 0;
  const pendingKey = String(pendingPlanChange?.targetPlanKey || "").toLowerCase();
  const pendingInterval = String(pendingPlanChange?.targetInterval || "").toLowerCase();
  const hasPendingToReplace = Boolean(pendingKey && pendingInterval);
  const pendingPlanLabel = hasPendingToReplace
    ? `${formatPlanDisplayName(pendingKey)} (${pendingInterval}${
        pendingPlanChange?.targetSeatCount
          ? `, ${pendingPlanChange.targetSeatCount} seat${
              pendingPlanChange.targetSeatCount === 1 ? "" : "s"
            }`
          : ""
      })`
    : "";
  const pendingEffectiveDate = pendingPlanChange?.effectiveAt
    ? new Date(pendingPlanChange.effectiveAt).toLocaleDateString()
    : "period end";

  if (!open) return null;

  const handleConfirm = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await onConfirm(seatCount);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md min-w-0 p-6 relative">
        <button
          type="button"
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          onClick={() => {
            if (isSubmitting) return;
            onClose();
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M1 1l12 12M13 1L1 13" />
          </svg>
        </button>

        <p className="text-lg font-semibold pr-8">
          {isSeatsMode
            ? "Manage seats"
            : isNewPlanMode
            ? isFixedSeatPlan
              ? "Confirm your plan"
              : "Choose your seats"
            : "Schedule plan change"}
        </p>
        {isSeatsMode ? (
          <p className="text-sm text-gray-600 mt-2">
            Professional includes <strong>5</strong> seats. Extra seats are{" "}
            <strong>
              {currencySymbol}
              {extraSeatPrice}
            </strong>{" "}
            each (charged now, then billed on every renewal).
          </p>
        ) : isNewPlanMode ? (
          <p className="text-sm text-gray-600 mt-2">
            {isTrial ? (
              <>
                Start your <strong>{targetPlanName}</strong> ({targetInterval}) plan with{" "}
                <strong>{trialDurationLabel}</strong>.
                {isLaunchFreeBilling
                  ? " No credit card required."
                  : " You won't be charged until the trial ends."}
              </>
            ) : (
              <>
                Start your <strong>{targetPlanName}</strong> ({targetInterval}) plan.
              </>
            )}
          </p>
        ) : (
          <p className="text-sm text-gray-600 mt-2">
            Switching to <strong>{targetPlanName}</strong> ({targetInterval}) at the end of your
            current billing period. Your current plan stays active until then.
          </p>
        )}

        {!isNewPlanMode && hasPendingToReplace ? (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            This will replace your scheduled change to <strong>{pendingPlanLabel}</strong> on{" "}
            <strong>{pendingEffectiveDate}</strong>.
          </div>
        ) : null}

        {isNewPlanMode ? null : isIntervalOnlyChange && !isFixedSeatPlan ? (
          <p className="text-xs text-gray-500 mt-2">
            Your {currentSeats} seat{currentSeats === 1 ? "" : "s"} will carry over unless you
            change them below.
          </p>
        ) : !isFixedSeatPlan ? (
          <p className="text-xs text-gray-500 mt-2">
            From {formatPlanDisplayName(currentPlanKey)} ({currentInterval}) with {currentSeats}{" "}
            seat{currentSeats === 1 ? "" : "s"}.
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-2">
            {targetPlanKey === "professional"
              ? `Professional includes ${getPlanIncludedSeats(targetPlanKey)} LinkedIn sender accounts.`
              : `Solo includes ${getPlanIncludedSeats(targetPlanKey)} LinkedIn sender account.`}
          </p>
        )}

        {!isFixedSeatPlan ? (
        <div className="mt-5 rounded-[6px] border border-light-grey bg-background-grey px-3 py-3">
          <p className="text-[10px] uppercase tracking-wide text-dark-grey/80">Sender seats</p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={isSubmitting || seatCount <= limits.min}
              onClick={() => setSeatCount((value) => Math.max(limits.min, value - 1))}
              className="h-8 w-8 rounded-md border border-light-grey bg-white text-mainColor disabled:opacity-40"
              aria-label="Decrease seats"
            >
              −
            </button>
            <span className="min-w-[88px] text-center text-[12px] font-medium text-mainColor">
              {seatCount} sender{seatCount === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              disabled={isSubmitting || seatCount >= limits.max}
              onClick={() => setSeatCount((value) => Math.min(limits.max, value + 1))}
              className="h-8 w-8 rounded-md border border-light-grey bg-white text-mainColor disabled:opacity-40"
              aria-label="Increase seats"
            >
              +
            </button>
          </div>
          <p className="text-[11px] text-dark-grey mt-2">
            {targetPlanKey === "professional"
              ? `5 seats included · ${currencySymbol}${extraSeatPrice} per extra account`
              : "Solo includes 1 LinkedIn sender account."}
          </p>
        </div>
        ) : null}

        <div className="mt-4 rounded-[6px] border border-light-grey bg-background-grey px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-dark-grey/80">
            {isSeatsMode ? "Billing" : "New plan total"}
          </p>
          <p className="text-[14px] text-mainColor font-semibold mt-0.5">
            {currencySymbol}
            {totalMonthly} / mo
          </p>
          {billedLine ? (
            <p className="text-[11px] text-dark-grey mt-0.5">{billedLine}</p>
          ) : null}
          {isSeatsMode && chargeNow > 0 ? (
            <p className="text-[11px] text-dark-grey mt-0.5">
              Pay {currencySymbol}
              {chargeNow} now for {seatDelta} extra seat{seatDelta === 1 ? "" : "s"}, then{" "}
              {currencySymbol}
              {totalMonthly}/mo on renewals.
            </p>
          ) : null}
          {isSeatsMode && chargeNow === 0 && seatCount < currentSeats ? (
            <p className="text-[11px] text-dark-grey mt-0.5">
              No refund for unused seats. Next renewal will be {currencySymbol}
              {totalMonthly}/mo.
            </p>
          ) : null}
          {isNewPlanMode && isTrial ? (
            <p className="text-[11px] text-dark-grey mt-0.5">
              {isLaunchFreeBilling
                ? "No payment required during the early joiner period."
                : `Billed after your ${trialDurationLabel}. Extra seats can be added after the trial.`}
            </p>
          ) : null}
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <Button
            variant="outline"
            size="sm"
            isDisabled={isSubmitting}
            onClick={() => {
              if (isSubmitting) return;
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" size="sm" isDisabled={isSubmitting} onClick={handleConfirm}>
            {isSubmitting
              ? isSeatsMode
                ? "Updating..."
                : isNewPlanMode
                ? "Redirecting..."
                : "Scheduling..."
              : isSeatsMode
              ? chargeNow > 0
                ? `Pay ${currencySymbol}${chargeNow} & update`
                : "Update seats"
              : isNewPlanMode
              ? isTrial
                ? isLaunchFreeBilling
                  ? "Start 1.5-month free trial"
                  : "Start 7-day free trial"
                : "Continue to checkout"
              : "Confirm change"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlanChangeModal;
